import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { gsap } from 'gsap';
import TracedWord from '../motion/TracedWord';
import '../../styles/hero.css';

const Hero = () => {
  const auroraRef = useRef(null);
  const titreRef = useRef(null);

  // L'ouverture : le nom se trace, puis se remplit. Un seul moment orchestré
  // sur toute la page d'accueil — c'est là que part toute l'audace.
  useLayoutEffect(() => {
    const el = titreRef.current;
    if (!el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = gsap.context(() => {
      const mots = gsap.utils.toArray('.trace-mot');
      gsap.set(mots, { '--trace': 0, '--plein': 0 });
      gsap
        .timeline({ defaults: { ease: 'power2.out' } })
        .to(mots, { '--trace': 1, duration: 0.85, stagger: 0.16 }, 0.15)
        .to(mots, { '--plein': 1, duration: 1.1, stagger: 0.18 }, 0.7);
    }, el);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        if (auroraRef.current && y < window.innerHeight * 1.2) {
          auroraRef.current.style.transform = `translate3d(0, ${y * 0.25}px, 0)`;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <header className="hero" id="hero">
      <div className="hero-aurora" ref={auroraRef} aria-hidden="true">
        <span className="aurora aurora--blue" />
        <span className="aurora aurora--gold" />
      </div>

      <div className="shell hero-inner">
        <p className="hero-place">Angers, France</p>

        <h1 className="hero-title" ref={titreRef}>
          <TracedWord text="Kévin" />
          <TracedWord text="Gelineau" className="hero-title-accent" />
        </h1>

        {/* Le métier se lit juste sous le nom : sur les portfolios qui
            fonctionnent, c'est l'offre qui occupe l'espace, pas l'identité. */}
        <p className="hero-role">UX/UI Designer et intégrateur front-end</p>

        <div className="hero-meta">
          <p className="hero-pitch">
            Je conçois des interfaces pensées pour les utilisateurs et prêtes pour les
            développeurs. Du wireframe Figma à l'intégration, sans friction.
          </p>
        </div>

        <div className="hero-foot">
          <a href="#work" className="hero-cue" aria-label="Aller aux projets">
            <span className="hero-cue-line" aria-hidden="true" />
            <span className="hero-cue-label">Projets réalisés</span>
            <ArrowDown size={16} aria-hidden="true" />
          </a>

          <p className="hero-status">
            <span className="hero-status-dot" aria-hidden="true" />
            Disponible en freelance et en agence
          </p>
        </div>
      </div>
    </header>
  );
};

export default Hero;
